"use client";

import React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { FiDownload, FiSearch } from "react-icons/fi";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import debounce from "lodash/debounce";
import Api from "../../config/Api";
import Loader from "../Widget/Loader";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

const ROW_HEIGHT = 40;
const PER_PAGE = 50;

const DownloadForm = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [totalPageAndRecords, setTotalPageAndRecords] = useState({
    totalPage: 0,
    totalRecords: 0,
  });
  const [error, setError] = useState("");
  const totalFetched = useRef(0);

  const fetchData = useCallback(
    async (pageNum = 1, reset = false, searchTerm = search) => {
      if (reset) {
        setIsSearching(true);
      } else {
        setIsLoading(true);
      }

      setError("");

      try {
        const res = await Api(`get-downloaded-sdrf-form?page=${pageNum}&per_page=${PER_PAGE}&search=${searchTerm}`);

        if (res?.data?.status === 1) {
          const newData = res.data.data.data;
          setData((prev) => (reset ? newData : [...prev, ...newData]));
          setTotalPageAndRecords({
            totalPage: res.data.data.last_page,
            totalRecords: res.data.data.total,
          });
          totalFetched.current = reset
            ? newData.length
            : totalFetched.current + newData.length;
          setHasMore(res.data.data.next_page_url !== null);
          setPage(pageNum + 1);
        } else {
          setError(res?.data?.message || "Failed to fetch data");
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setError("Something went wrong while loading data.");
        }
      } finally {
        setIsLoading(false);
        setIsSearching(false);
      }
    },
    [search]
  );

  // const handleManualPageChange = (newPage) => {
  //   if (newPage !== page && newPage > 0) {
  //     totalFetched.current = 0;
  //     setData([]); // clear current list
  //     fetchData(newPage, true); // load page manually
  //   }
  // };
  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      setPage(1);
      totalFetched.current = 0;
      setData([]);
      setSearch(searchValue);
      fetchData(1, true, searchValue);
    }, 100),
    [fetchData]
  );

  // Initial data fetch
  useEffect(() => {
    fetchData(1, true);
  }, []); // Remove fetchData from dependencies to avoid infinite loop

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    debouncedSearch(value);
  };

  // Load more data when scrolling
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore && data.length > 0) {
      console.log("Loading more data, page:", page);
      fetchData(page);
    }
  }, [fetchData, page, isLoading, hasMore, data.length]);

  const Row = ({ index, style }) => {
    const item = data[index];

    // Trigger load more when near the end
    if (hasMore && index === data.length - 5 && !isLoading) {
      loadMore();
    }

    if (!item) {
      return (
        <div
          style={{
            ...style,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="text-muted">Loading...</div>
        </div>
      );
    }

    return (
      <div
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          padding: "0.75rem 0.5rem",
          borderBottom: "1px solid #eee",
          fontSize: "0.800rem",
        }}
        className="hover:bg-gray-50"
      >
        <div
          style={{ width: "60px", textAlign: "center" }}
          className="text-muted"
        >
          {index + 1}
        </div>
        <div
          style={{ flex: 1, paddingRight: "0.5rem" }}
          className="font-medium"
        >
          {item.application_no}
        </div>
        <div style={{ flex: 1, paddingRight: "0.5rem" }}>
          {item.district?.district_name || "N/A"}
        </div>
        <div
          style={{ flex: 1, paddingRight: "0.5rem" }}
          className="text-blue-600"
        >
          {item.user?.email_id || "N/A"}
        </div>
        <div
          style={{ flex: 1, paddingRight: "0.5rem" }}
          className="font-mono text-sm"
        >
          {item.user_ip || "N/A"}
        </div>
        <div style={{ flex: 1, paddingRight: "0.5rem" }}>
          {item.created_at
            ? new Date(item.created_at).toLocaleDateString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A"}
        </div>
        <div style={{ flex: 0.8 }}>
          <span
            className={`px-2 py-1 rounded-full text-xs ${
              item.user?.role === "1"
                ? "bg-green-100 text-green-800"
                : "bg-blue-100 text-blue-800"
            }`}
          >
            {item.user?.role === "1" ? "Admin" : "User"}
          </span>
        </div>
      </div>
    );
  };

  const TableHeader = () => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "0.5rem 0.5rem",
        fontWeight: "600",
        borderBottom: "2px solid #e5e7eb",
        backgroundColor: "#f9fafb",
        fontSize: "0.875rem",
      }}
    >
      <div style={{ width: "60px", textAlign: "center" }}>#</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>Application ID</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>District</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>Email</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>IP Address</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>Download Date</div>
      <div style={{ flex: 0.8 }}>User Type</div>
    </div>
  );
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border">
      {/* Header */}
      <div className="d-md-flex justify-content-between mb-3 align-items-center">
        <h6 className="text-dark m-0">
          Downloaded SDRF Forms
        </h6>
        <div className="d-flex gap-2 flex-wrap">
          <div>
            <a
              href="https://nsdmautilityforms.org/"
              rel="noreferrer"
              target="_blank"
              className="btn btn-success btn-sm"
            >
              <FiDownload className="me-2" />
              Download Form
            </a>
          </div>
          <div>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ width: "250px" }}
              placeholder="Search by ID, email, district..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Table */}
      {data.length > 0 ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <TableHeader />
          <div style={{ height: "400px" }}>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  height={height}
                  itemCount={data.length}
                  itemSize={ROW_HEIGHT}
                  width={width}
                >
                  {Row}
                </List>
              )}
            </AutoSizer>
          </div>

          {isLoading && (
            <div className="border-t border-gray-200 p-4">
              <Loader title="Loading..." />
            </div>
          )}

          <div className="d-flex justify-content-end align-items-center p-4 border-t bg-gray-50 text-sm">
     
            {/* <button
              className="btn btn-light btn-sm me-2 ms-4 border"
              onClick={() => handleManualPageChange(page - 1)}
              disabled={page <= 1}
            >
              <FaArrowLeft />
            </button> */}
            <span className="text-sm text-gray-500">
              Total Records: {totalPageAndRecords.totalRecords.toLocaleString()} | Page {page} of {totalPageAndRecords.totalPage}
            </span>

            {/* <button
              className="btn btn-light btn-sm ms-2 border"
              onClick={() => handleManualPageChange(page + 1)}
              disabled={!hasMore}
            >
              <FaArrowRight />
            </button> */}
          </div>
        </div>
      ) : isSearching || isLoading ? (
        <Loader title="Loading..." />
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-2">No records found</div>
          <div className="text-gray-400 text-sm">
            {search ? `No results for "${search}"` : "No data available"}
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadForm;
